<script>
import UserRoleForm from "@/components/Admin/UserRoleForm.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import { userLink } from "@/helpers/links";
import { canManageGlobalRoles } from "@/helpers/permissions/users.js";
import { del, post } from "@/requests/server";

export default {
  name: "UserRolesList",
  components: {
    IconButton,
    PaginatedTable,
    UserRoleForm,
    ValidatedButton,
  },
  props: {
    // The user for which we manipulate roles.
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editedRole: null,
      columns: [
        // Necessary to have a unique id for rows in the list.
        new Column("id", capitalize(this.$t("lists.id")), "id", {
          showByDefault: false,
        }),
        Column.withRelation(
          "user.full_name",
          this.$t("user_roles.fields.user"),
          {
            relation: "users",
            label: "full_name",
            field: "id",
          },
          "user_id",
          {
            showByDefault: false,
            urlFct: (userRole) => userLink(userRole.user),
          }
        ),
        Column.withSelect("role", this.$t("user_roles.fields.role.title"), [
          { value: null, label: this.$t("user_roles.fields.role.options.null.all") },
          {
            value: "accountant",
            label: this.$t("user_roles.fields.role.options.accountant"),
          },
          {
            value: "car_sharing_manager",
            label: this.$t("user_roles.fields.role.options.car_sharing_manager"),
          },
          {
            value: "admin",
            label: this.$t("user_roles.fields.role.options.admin"),
            variant: "danger",
          },
        ]),
        Column.withRelation(
          "granted_by_user.full_name",
          this.$t("user_roles.fields.granted_by"),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              for: "profile",
            },
          },
          "granted_by_user_id",
          {
            urlFct: (userRole) => userLink(userRole.granted_by_user),
          }
        ),
      ],
    };
  },
  computed: {
    accessingUser() {
      return this.$store.state.user;
    },
    canAddRole() {
      return canManageGlobalRoles(this.accessingUser);
    },
  },
  methods: {
    canEditRole() {
      // Roles don't have parameters, therefore are not editable
      return false;
    },
    canDeleteRole(role) {
      // Don't allow deleting own admin role.
      if (role.role === "admin" && role.user_id === this.accessingUser.id) {
        return false;
      }

      return canManageGlobalRoles(this.accessingUser);
    },
    beginEditRole(role = null) {
      if (!role) {
        this.editedRole = {
          user_id: this.user.id,
          user: this.user,
        };
      } else {
        this.editedRole = role;
      }

      // Can only edit role if creating a new one. Otherwise, one must delete and add a new role.
      this.$refs.userRoleEditModal.canEditRole = !this.editedRole.id;
      this.$refs.userRoleEditModal.canEditUser = false;

      this.$refs.userRoleEditModal.show();
    },
    async saveRole(editedRole) {
      // Data we get back from the server after saving.
      let updatedRole = null;

      if (editedRole.id) {
        // Editing is not implemented yet, since roles don't have editable parameters.
      } else {
        // A new role is created.
        const { data } = await post(
          `/users/${editedRole.user_id}/roles`,
          {
            role: editedRole.role,
            user_id: editedRole.user_id,
          },
          {
            notifications: {
              action: this.$t("user_roles.notifications.add_role.action"),
              onSuccess: this.$t("user_roles.notifications.add_role.success"),
            },
          }
        );
        updatedRole = data;

        // Emit added so the parent can account for the change.
        this.$emit("added", updatedRole);
      }

      this.$refs.userRolesTable.refresh();
    },
    async deleteRole(role) {
      await del(`/users/roles/${role.id}`, {
        notifications: {
          action: this.$t("user_roles.notifications.delete_role.action"),
          onSuccess: this.$t("user_roles.notifications.delete_role.success"),
        },
      });

      this.$refs.userRolesTable.refresh();

      // Emit deleted so the parent can account for the change.
      this.$emit("deleted", role);
    },
  },
};
</script>
<template>
  <div>
    <paginated-table
      ref="userRolesTable"
      :endpoint="`/users/${user.id}/roles`"
      :columns="columns"
      :show-generate-csv="false"
    >
      <template #head-buttons>
        <icon-button v-if="canAddRole" role="add" @click="() => beginEditRole()">{{
          $t("user_roles.actions.add")
        }}</icon-button>
      </template>

      <template #cell(actions)="{ item }">
        <icon-button
          v-if="canEditRole(item)"
          role="edit"
          size="sm"
          @click="() => beginEditRole(item)"
        >
        </icon-button>
        <validated-button
          v-if="canDeleteRole(item)"
          :action="async () => deleteRole(item)"
          :question="
            $t('user_roles.delete_role_confirmation', {
              role: $t(`user_roles.fields.role.options.${item.role}`),
            })
          "
          :label="$t('user_roles.forms.revoke')"
          icon="trash"
          size="sm"
          variant="ghost-danger"
          :show-button-label="false"
        />
      </template>
    </paginated-table>

    <user-role-form ref="userRoleEditModal" :value="editedRole" @save="saveRole" />
  </div>
</template>

<style lang="scss"></style>
